import { useHasAccess } from 'lib-shared-access-control';
import { MetaConversation } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useServerStorage } from 'utils/useServerStorage';

import ContactCard from '../../cards/ContactCard';
import { ConversationCustomFieldsCard } from '../../cards/customFields';
import { HistoryCard } from '../../cards/historyCard/HistoryCard';
import { UserTrackingCard } from '../../cards/userTrackingCard';
import {
  defaultCustomerInfoOrder,
  SidebarCardId,
} from '../../conversationSidebar.types';
import { mergeOrder, reorderArray } from '../../utils/reorder';
import { CardSection } from '../cardSection/cardSection';

type CustomerInfoCardsProps = {
  index: number;
  metaConversation: MetaConversation;
  isMinimized: boolean;
  isCardOpen: (card: string) => boolean;
  toggleCard: (card: string) => void;
};

const type = Symbol('customerInfoCard');

export const CustomerInfoCards = ({
  index,
  toggleCard,
  isCardOpen,
  isMinimized,
  metaConversation,
}: CustomerInfoCardsProps): JSX.Element => {
  const flags = useFeatureFlag([
    'collapse-tracking-card-core',
    'show-tracking-card',
  ]);
  const hasCustomAttributeAccess = useHasAccess(
    'conversation.custom_attribute',
  );

  const storage = useServerStorage('user');
  const cardOrder = useMemo(
    () =>
      mergeOrder(
        storage.data.conversation?.sidebarCustomerInfoOrder,
        defaultCustomerInfoOrder,
      ),
    [storage.data.conversation?.sidebarCustomerInfoOrder],
  );

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      storage.set({
        conversation: (draft) => {
          draft.sidebarCustomerInfoOrder = reorderArray(
            storage.data.conversation?.sidebarCustomerInfoOrder,
            defaultCustomerInfoOrder,
            dragIndex,
            hoverIndex,
          );
        },
      });

      ampli.conversationRightSidebarItemReordered({
        sidebarItemType: 'card',
      });
    },
    [storage],
  );

  const renderCard = (cardId: SidebarCardId, index: number) => {
    switch (cardId) {
      case 'contact':
        return (
          <ContactCard
            key={cardId}
            onToggle={() => toggleCard('contact')}
            isOpen={isCardOpen('contact')}
            isMinimized={isMinimized}
            index={index}
            type={type}
            onMove={handleMove}
          />
        );
      case 'custom-fields':
        return hasCustomAttributeAccess && metaConversation.conversation.id ? (
          <ConversationCustomFieldsCard
            key={cardId}
            csid={metaConversation.conversation.id}
            isMinimized={isMinimized}
            isOpen={isCardOpen('custom-fields')}
            onToggle={() => toggleCard('custom-fields')}
            index={index}
            type={type}
            onMove={handleMove}
          />
        ) : null;
      case 'history':
        return metaConversation.requester &&
          metaConversation.previousConversations &&
          metaConversation.previousConversations?.length !== 0 ? (
          <HistoryCard
            key={cardId}
            onToggle={() => toggleCard('history')}
            isOpen={isCardOpen('history')}
            previousConversations={metaConversation.previousConversations}
            requester={metaConversation.requester}
            isMinimized={isMinimized}
            index={index}
            type={type}
            onMove={handleMove}
          />
        ) : null;
      case 'tracking':
        return flags.showTrackingCard ? (
          <UserTrackingCard
            key={cardId}
            onToggle={() => toggleCard('tracking')}
            isOpen={isCardOpen('tracking')}
            attributes={metaConversation.conversation.attributes ?? {}}
            isMinimized={isMinimized}
            hasCollapseTrackingCard={flags.collapseTrackingCardCore}
            index={index}
            type={type}
            onMove={handleMove}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <CardSection
      sectionId="customerInfo"
      index={index}
      isMinimized={isMinimized}
    >
      {cardOrder.map((id, idx) => renderCard(id, idx))}
    </CardSection>
  );
};
